import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Send,
  MapPin,
  Clock,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  Copy,
  Check,
  Building2,
  Sparkles,
  Loader2,
} from "lucide-react";

import Seo from "../../Components/SEO/Seo";
import Container from "../../Components/Container/Container";
import Button from "../../Components/Button/Button";
import Partners from "../../Components/Home/Partners/Partners";

import { contactData } from "../../Data/contactData";
import { fadeUp, fadeLeft, fadeRight, viewportOnce } from "../../Utils/animations";
import "../../Style/contact/contact.css";

const SERVICE_TAGS = [
  "B2B Company Data",
  "Data Validation",
  "Data Verification",
  "Data Appending",
  "Appointment Setting",
  "Email Marketing",
  "Lead generation",
  "Company Profiling",
];

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "B2B Company Data",
    subject: "",
    message: "",
  });

  const [status, setStatus] = useState({ loading: false, success: false, error: null });
  const [copiedValue, setCopiedValue] = useState(null);
  const [indiaTime, setIndiaTime] = useState("");
  const [usaTime, setUsaTime] = useState("");

  // Live Time Calculation for Global Hubs
  useEffect(() => {
    const updateTimes = () => {
      const now = new Date();
      setIndiaTime(
        now.toLocaleTimeString("en-US", {
          timeZone: "Asia/Kolkata",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
      );
      setUsaTime(
        now.toLocaleTimeString("en-US", {
          timeZone: "America/New_York",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
      );
    };

    updateTimes();
    const interval = setInterval(updateTimes, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopiedValue(label);
    setTimeout(() => setCopiedValue(null), 2000);
  };

  // PHPMailer POST handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, success: false, error: null });

    try {
      const response = await fetch("/api/send-contact.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok && result.status === "success") {
        setStatus({
          loading: false,
          success: true,
          error: null,
        });

        // Reset inputs
        setFormData({
          name: "",
          email: "",
          phone: "",
          service: "B2B Company Data",
          subject: "",
          message: "",
        });
      } else {
        setStatus({
          loading: false,
          success: false,
          error: result.message || "Failed to submit inquiry. Please try again.",
        });
      }
    } catch (err) {
      setStatus({
        loading: false,
        success: false,
        error: "Unable to reach the email server. Please check your connection or email directly.",
      });
    }
  };

  return (
    <div className="contact-page">
      <Seo
        title="Contact Us | Conectify Tech"
        description="Get in touch with Conectify Tech. Reach our USA or India office for B2B data verification, pipeline building, and staffing inquiries."
      />

      <div className="contact-blob contact-blob--blue" />
      <div className="contact-blob contact-blob--cyan" />

      <main id="main">
        {/* 1. Header Section */}
        <section className="contact-hero">
          <Container>
            <motion.div
              className="contact-hero__content text-center max-w-3xl mx-auto"
              variants={fadeUp()}
              initial="hidden"
              whileInView="show"
              viewport={viewportOnce}
            >
              <span className="section-eyebrow eye">
                <span className="section-eyebrow__dot" aria-hidden="true" />
                {contactData.hero.eyebrow}
              </span>
              <h1 className="section-title mt-4">
                {contactData.hero.title}{" "}
                <span className="text-gradient">{contactData.hero.highlight}</span>
              </h1>
              <p className="contact-hero__desc">{contactData.hero.description}</p>
            </motion.div>

            {/* Quick Contact Cards */}
            <div className="contact-methods">
              {contactData.directContacts.map((item, idx) => {
                const Icon = item.icon;
                const isLink = Boolean(item.href);
                const isCopied = copiedValue === item.label;

                return (
                  <motion.div
                    key={item.label}
                    variants={fadeUp(idx * 0.1)}
                    initial="hidden"
                    whileInView="show"
                    viewport={viewportOnce}
                    className="contact-method-wrapper"
                  >
                    <div className="contact-method-card">
                      <div className="contact-method-card__top">
                        <div className="contact-method-card__icon">
                          <Icon size={22} />
                        </div>
                        {isLink && (
                          <button
                            type="button"
                            className="copy-btn"
                            onClick={() => handleCopy(item.value, item.label)}
                            title="Copy to clipboard"
                          >
                            {isCopied ? <Check size={14} className="text-success" /> : <Copy size={14} />}
                          </button>
                        )}
                      </div>
                      <span className="contact-method-card__label">{item.label}</span>
                      {isLink ? (
                        <a href={item.href} className="contact-method-card__value">
                          {item.value}
                        </a>
                      ) : (
                        <span className="contact-method-card__value">{item.value}</span>
                      )}
                      <span className="contact-method-card__sub">{item.subtext}</span>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </Container>
        </section>

        {/* 2. Form and Global Locations Grid */}
        <section className="contact-main">
          <Container>
            <div className="contact-grid">
              {/* Left Column: Direct Inquiry Form */}
              <motion.div
                className="contact-form-box"
                variants={fadeLeft()}
                initial="hidden"
                whileInView="show"
                viewport={viewportOnce}
              >
                <div className="contact-form-box__header">
                  <div className="flex items-center gap-2">
                    <Sparkles size={18} className="text-primary" />
                    <h2 className="text-xl font-bold">Initiate an Enterprise Inquiry</h2>
                  </div>
                  <p className="text-sm text-slate-500 mt-1">
                    Fill out the form below. Our corporate solutions team will respond within 24 hours.
                  </p>
                </div>

                <form className="contact-form" onSubmit={handleSubmit}>
                  {/* Service Intent Selector */}
                  <div className="contact-service-select">
                    <label className="contact-form__label">Area of Interest</label>
                    <div className="service-chips-grid">
                      {SERVICE_TAGS.map((tag) => (
                        <button
                          key={tag}
                          type="button"
                          className={`service-chip ${formData.service === tag ? "is-active" : ""}`}
                          onClick={() => setFormData((prev) => ({ ...prev, service: tag }))}
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="contact-form__group-row">
                    <div className="contact-form__field">
                      <label htmlFor="name" className="contact-form__label">Your Name *</label>
                      <input
                        id="name"
                        type="text"
                        name="name"
                        required
                        placeholder="John Doe"
                        className="contact-form__input"
                        value={formData.name}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="contact-form__field">
                      <label htmlFor="email" className="contact-form__label">Business Email *</label>
                      <input
                        id="email"
                        type="email"
                        name="email"
                        required
                        placeholder="john@company.com"
                        className="contact-form__input"
                        value={formData.email}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="contact-form__group-row">
                    <div className="contact-form__field">
                      <label htmlFor="phone" className="contact-form__label">Phone Number</label>
                      <input
                        id="phone"
                        type="tel"
                        name="phone"
                        placeholder="+1 (555) 000-0000"
                        className="contact-form__input"
                        value={formData.phone}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="contact-form__field">
                      <label htmlFor="subject" className="contact-form__label">Subject</label>
                      <input
                        id="subject"
                        type="text"
                        name="subject"
                        placeholder="e.g. Lead Validation Scope"
                        className="contact-form__input"
                        value={formData.subject}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="contact-form__field">
                    <label htmlFor="message" className="contact-form__label">Message / Project Scope *</label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      placeholder="Specify your target criteria, timeline, required fields, or dataset volume..."
                      className="contact-form__textarea"
                      value={formData.message}
                      onChange={handleChange}
                    />
                  </div>

                  {/* Submission State Banners */}
                  <AnimatePresence>
                    {status.success && (
                      <motion.div
                        className="form-feedback-alert form-feedback-alert--success"
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                      >
                        <CheckCircle2 size={16} />
                        <span>Inquiry transmitted successfully. A specialist has been assigned to your request.</span>
                      </motion.div>
                    )}
                    {status.error && (
                      <motion.div
                        className="form-feedback-alert"
                        style={{
                          background: "rgba(239, 68, 68, 0.1)",
                          color: "#dc2626",
                          border: "1px solid rgba(239, 68, 68, 0.3)",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          padding: "12px",
                          borderRadius: "8px",
                          fontSize: "0.875rem",
                          marginBottom: "1rem",
                        }}
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                      >
                        <AlertCircle size={16} />
                        <span>{status.error}</span>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="contact-form__footer">
                    <Button
                      size="md"
                      icon={status.loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                      type="submit"
                      disabled={status.loading}
                    >
                      {status.loading ? "Routing Inquiry..." : "Submit Enterprise Inquiry"}
                    </Button>

                    <div className="form-sla-note">
                      <ShieldCheck size={14} className="text-primary" />
                      <span>Confidentiality &amp; Non-Disclosure Protected</span>
                    </div>
                  </div>
                </form>
              </motion.div>

              {/* Right Column: Physical Offices with Local Clocks */}
              <motion.div
                className="contact-offices"
                variants={fadeRight(0.1)}
                initial="hidden"
                whileInView="show"
                viewport={viewportOnce}
              >
                <div className="contact-offices__header">
                  <div className="flex items-center gap-2">
                    <Building2 size={18} className="text-primary" />
                    <h3 className="text-lg font-bold">Operational Headquarters</h3>
                  </div>
                  <p className="text-sm text-slate-500">Coordinate directly with our global dispatch locations.</p>
                </div>

                {contactData.offices.map((office) => {
                  const isUSA = office.country.toLowerCase().includes("usa") || office.country.toLowerCase().includes("united states");
                  const localTime = isUSA ? usaTime : indiaTime;

                  return (
                    <div key={office.region} className="office-card">
                      <div className="office-card__header">
                        <div className="office-card__title">
                          <span className="flag-icon">{office.flag}</span>
                          <span className="region-name">{office.region}</span>
                        </div>
                        <span className="office-card__tag">{office.country}</span>
                      </div>

                      <div className="office-card__clock">
                        <Clock size={13} className="text-primary" />
                        <span>Local Hub Time: <strong>{localTime || "Syncing..."}</strong></span>
                      </div>

                      <div className="office-card__address">
                        <MapPin size={18} className="text-primary flex-shrink-0 mt-0.5" />
                        <div>
                          <p>{office.address}</p>
                          <p className="font-semibold text-slate-900 dark:text-slate-100 mt-1">
                            {office.city}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Service SLA Overview Card */}
                <div className="contact-guarantee-card">
                  <h4>Conectify Tech Response SLAs</h4>
                  <ul className="sla-list">
                    <li>
                      <CheckCircle2 size={14} className="text-success" />
                      <span>Dedicated account manager assigned within 2 business hours</span>
                    </li>
                    <li>
                      <CheckCircle2 size={14} className="text-success" />
                      <span>Complimentary data verification sample audits on request</span>
                    </li>
                    <li>
                      <CheckCircle2 size={14} className="text-success" />
                      <span>Strict compliance with international opt-in standards</span>
                    </li>
                  </ul>
                </div>
              </motion.div>
            </div>
          </Container>
        </section>

        {/* 3. Partner Badges */}
        <Partners />
      </main>
    </div>
  );
}